import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { useCalendarAreaId } from "../../context/CalendarAreaContext";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { DraftSnapshot } from "../../models/Event";
import { getDraftSnapshots } from "../../repositories/CalendarRepository";

interface RestoreDraftSnapshotModalProps {
	open: boolean;
	externalLoading: boolean;
	onHide: () => void;
	onRestore: (snapshotId: number) => void;
}

const formatDate = (date: Date | null): string => {
	if (!date) return "-";
	return date.toLocaleString("en-US", {
		month: "short",
		day: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const formatDateRange = (start: Date | null, end: Date | null): string => {
	if (!start && !end) return "No events";
	const startStr = start
		? start.toLocaleDateString("en-US", { month: "short", day: "2-digit" })
		: "?";
	const endStr = end
		? end.toLocaleDateString("en-US", { month: "short", day: "2-digit" })
		: "?";
	return `${startStr} - ${endStr}`;
};

const getReasonColor = (reason: string): "warning" | "info" | "secondary" => {
	switch (reason) {
		case "RESET":
			return "warning";
		case "RESTORE":
			return "secondary";
		case "MANUAL":
		default:
			return "info";
	}
};

export const RestoreDraftSnapshotModal = ({
	open,
	externalLoading,
	onHide,
	onRestore,
}: RestoreDraftSnapshotModalProps) => {
	const defaultAreaId = useCalendarAreaId();
	const [error, setError] = useState("");
	const [selectedSnapshotId, setSelectedSnapshotId] = useState<number | null>(
		null,
	);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);

	const {
		data: snapshots,
		isLoading,
		error: queryError,
		isError,
		refetch,
	} = useQuery({
		queryKey: ["draftSnapshots", defaultAreaId],
		queryFn: ({ signal }) => getDraftSnapshots({ defaultAreaId }, signal),
		enabled: open,
	});

	const handleRowClick = (snapshotId: number) => {
		if (selectedSnapshotId === snapshotId) {
			setSelectedSnapshotId(null);
		} else {
			setSelectedSnapshotId(snapshotId);
		}
	};

	const handleRestoreClick = () => {
		if (selectedSnapshotId === null) return;
		setConfirmModalOpen(true);
	};

	const handleConfirmRestore = () => {
		if (selectedSnapshotId === null) return;
		onRestore(selectedSnapshotId);
		setConfirmModalOpen(false);
	};

	const handleClose = () => {
		setSelectedSnapshotId(null);
		onHide();
	};

	const selectedSnapshot = snapshots?.find(
		(s) => s.SnapshotId === selectedSnapshotId,
	);

	useEffect(() => {
		if (queryError && isError) {
			setError(
				queryError instanceof Error ? queryError.message : "Unknown error",
			);
		}
	}, [queryError, isError]);

	useEffect(() => {
		if (open) {
			refetch();
		}
	}, [open, refetch]);

	return (
		<>
			<MesfModal
				open={open}
				handleClose={handleClose}
				title="Restore Draft from Snapshot"
				maxWidth="lg"
				fullWidth
			>
				<MesfModal.Content dividers>
					{isLoading ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								py: 4,
							}}
						>
							<CircularProgress />
						</Box>
					) : !snapshots || snapshots.length === 0 ? (
						<Typography color="textSecondary" align="center" sx={{ py: 4 }}>
							No snapshots available. Snapshots are created automatically before
							each reset.
						</Typography>
					) : (
						<>
							<Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
								Select a snapshot to restore. Your current draft state will be
								backed up automatically before restoration.
							</Typography>
							<TableContainer sx={{ maxHeight: "50vh" }}>
								<Table stickyHeader size="small">
									<TableHead>
										<TableRow>
											<TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
											<TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
											<TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
											<TableCell sx={{ fontWeight: 600 }} align="center">
												Events
											</TableCell>
											<TableCell sx={{ fontWeight: 600 }}>
												Event Range
											</TableCell>
											<TableCell sx={{ fontWeight: 600 }}>
												Description
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{snapshots.map((snapshot: DraftSnapshot) => (
											<TableRow
												key={snapshot.SnapshotId}
												hover
												selected={selectedSnapshotId === snapshot.SnapshotId}
												onClick={() => handleRowClick(snapshot.SnapshotId)}
												sx={{
													cursor: "pointer",
													"&.Mui-selected": {
														backgroundColor: "primary.light",
														"&:hover": {
															backgroundColor: "primary.light",
														},
													},
												}}
											>
												<TableCell>
													<Typography variant="body2">
														{formatDate(snapshot.CreatedAt)}
													</Typography>
												</TableCell>
												<TableCell>
													<Chip
														label={snapshot.Reason}
														color={getReasonColor(snapshot.Reason)}
														size="small"
														variant="outlined"
													/>
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{snapshot.CreatedBy}
													</Typography>
												</TableCell>
												<TableCell align="center">
													<Typography variant="body2">
														{snapshot.EventCount}
														{snapshot.ProductionEventCount > 0 && (
															<Typography
																component="span"
																variant="caption"
																color="textSecondary"
															>
																{" "}
																({snapshot.ProductionEventCount} prod)
															</Typography>
														)}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{formatDateRange(
															snapshot.FirstEventStart,
															snapshot.LastEventEnd,
														)}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography
														variant="body2"
														color="textSecondary"
														sx={{
															maxWidth: 200,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}
													>
														{snapshot.Description || "-"}
													</Typography>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>

							<Box
								sx={{
									mt: 2,
									pt: 2,
									borderTop: "1px solid",
									borderColor: "divider",
								}}
							>
								<Typography variant="caption" color="textSecondary">
									<strong>Note:</strong> Snapshots are created automatically
									before each reset and when restoring. Maximum 20 snapshots are
									kept; older ones are automatically deleted.
								</Typography>
							</Box>
						</>
					)}
				</MesfModal.Content>

				<MesfModal.Actions sx={{ px: 3, py: 2 }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							width: "100%",
						}}
					>
						<Button
							variant="outlined"
							onClick={handleClose}
							disabled={isLoading || externalLoading}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={handleRestoreClick}
							disabled={
								selectedSnapshotId === null || isLoading || externalLoading
							}
						>
							Restore Selected Snapshot
						</Button>
					</Box>
				</MesfModal.Actions>
			</MesfModal>

			<ConfirmationDialogRaw
				title="Confirm Restore"
				message={
					selectedSnapshot
						? `Are you sure you want to restore from the snapshot created on ${formatDate(selectedSnapshot.CreatedAt)}?\n\nThis will:\n- Create a backup of your current draft state\n- Replace current draft with ${selectedSnapshot.EventCount} events from the snapshot`
						: "Are you sure you want to restore from this snapshot?"
				}
				open={confirmModalOpen}
				onClose={(value?: string) => {
					if (value === "OK") {
						handleConfirmRestore();
					}
					setConfirmModalOpen(false);
				}}
			/>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
